import axios from "axios";
import { call, put, takeEvery, select } from "redux-saga/effects";
import constants from "constants";
import actions from "actions";
import { toast } from "react-toastify";
import { push, goBack } from "redux-router";
import validator from "services/validator.js";
import storage from "services/storage";
import { UPDATE_STUDYGROUP_SUPERHOST } from "../../constants/actions";

const {
  addRequestError,
  addErrorsSaga,
  addValidateError,
  cleanErrors,
  startRequest,
  endRequest,
  saveStudygroups,
  setStudygroup,
  setAdminGroupMembers,
  setGroupInvites,
  deleteInviteSuccess,
  getCourseMaterials,
  setCourseMaterials,
  setGroupMembers,
  setGroupMaterialInfinity,
  updateGroupMaterialsAfterDelete,
  setGroupMaterials,
  setStudyGroupInvites,
  setStudyGroupStatistic,
  setStudyGroupWebinars,
  saveStudygroupsInfinite,
  hideModal,
  updateStudyGroupRequest,
  userCancelRequestStudyGroup,
} = actions;

const {
  CREATE_STUDYGROUP,
  UPDATE_STUDYGROUP,
  DELETE_STUDYGROUPS,
  API_ADMIN_STUDY_GROUPS,
  GET_STUDYGROUP,
  GET_ADMIN_STUDYGROUP,
  GET_STUDYGROUPS,
  GET_ADMIN_GROUP_MEMBERS,
  API_ADMIN_STUDY_GROUP_MEMBERS,
  SEND_GROUP_INVITES,
  GET_GROUP_INVITES,
  API_ADMIN_STUDY_GROUP_INVITES,
  DELETE_INVITE,
  GET_COURSE_MATERIALS,
  CREATE_COURSE_MATERIAL,
  UPDATE_COURSE_MATERIAL,
  DELETE_COURSE_MATERIALS,
  API_ADMIN_GROUP_MATERIALS,
  GET_STUDYGROUPS_ADMIN,
  GET_GROUP_MEMBERS,
  API_STUDY_GROUP_MEMBERS,
  API_ADMIN_STUDY_GROUP_MATERIALS,
  API_STUDY_GROUP_MATERIALS,
  LOAD_GROUP_MATERIAL,
  CREATE_STUDY_GROUP_MATERIALS,
  GET_GROUP_MATERIAL,
  DELETE_STUDY_GROUP_MATERIAL,
  GET_STUDY_GROUP_INVITES,
  API_STUDY_GROUPS,
  API_GROUP_INVITES,
  GET_STUDY_GROUP_STATISTIC,
  API_DASHBOARDS_WEBINARS,
  GET_STUDY_GROUP_WEBINARS,
  API_ADMIN_WEBINARS,
  DELETE_STUDY_GROUP_WEBINARS,
  JOIN_GROUP_FROM_LINK,
  DELETE_GROUP_MEMBER,
  DELETE_GROUP_INVITE,
  GET_STUDYGROUPS_INFINITE,
  USER_LEAVE_GROUP,
  API_LEAVE_GROUP,
  USER_REQUEST_TO_JOIN_GROUP,
  API_USER_REQUEST_TO_JOIN_GROUP,
  USER_CANCEL_REQUEST_STUDY_GROUP,
} = constants;

const getUrl = (params = {}) => {
  const keys = Object.keys(params);

  return keys.map((key) => `&${key}=${params[key]}`).join("");
};

const formatRequestData = (requestData, key) => {
  let data = new FormData();

  Object.keys(requestData).forEach((field) => {
    if (requestData[field]) data.append(`${key}[${field}]`, requestData[field]);
  });

  return data;
};

const getGroupMaterialFormData = (data) => {
  const formdata = new FormData();

  Object.keys(data).forEach((field) => {
    formdata.append(`group_material[${field}]`, data[field]);
  });

  return formdata;
};

const getStudyGroupFormData = ({
  title,
  superhost_ids,
  show_for_users,
  logo,
}) => {
  const formaData = new FormData();

  formaData.append("group[title]", title);
  formaData.append("group[show_for_users]", show_for_users);
  if (logo) formaData.append("group[logo]", logo);

  for (const id of superhost_ids) {
    formaData.append("group[superhost_ids][]", id);
  }

  return formaData;
};

const apiUserRequestToJoinGroup = (groupId) =>
  axios.post(`${API_USER_REQUEST_TO_JOIN_GROUP}`, {
    group_invite: {
      group_id: groupId,
    },
  });

const apiCreateStudygroup = (studygroup) =>
  axios.post(API_ADMIN_STUDY_GROUPS, studygroup);

const apiGetStudygroup = (id) => axios.get(`${API_STUDY_GROUPS}/${id}`);

const apiAdminGetStudygroup = (id) =>
  axios.get(`${API_ADMIN_STUDY_GROUPS}/${id}`);

const apiLoadStudyGroupsMaterial = (params) =>
  axios.get(API_STUDY_GROUP_MATERIALS, { params });

const apiDeleteStudyGroupsMaterials = (id) =>
  axios.delete(`${API_STUDY_GROUP_MATERIALS}${id}`);

const apiCreateStudyGroupMaterial = async (payload) =>
  await axios.post(
    API_STUDY_GROUP_MATERIALS,
    getGroupMaterialFormData(payload)
  );

const apiLeaveGroupUser = (id) => axios.get(`${API_LEAVE_GROUP}/${id}`);

const apiGetStudyGroupInvites = (params) =>
  axios.get(API_GROUP_INVITES, { params });

const apiGetStudyGroupStatistic = (params) =>
  axios.get(API_DASHBOARDS_WEBINARS, { params });

const apiGetStudyGroupWebinars = (params) =>
  axios.get(API_ADMIN_WEBINARS, { params });

const apiJoinStudyGroup = (id) => axios.put(`${API_GROUP_INVITES}/${id}`);

const apiDeleteWebinars = (webinar_ids) => {
  return axios.post(
    API_ADMIN_WEBINARS +
      "/destroy" +
      `?${webinar_ids.map(
        (item, index) => `${index > 0 ? "&" : ""}webinar_ids[]=${item}`
      )}`
  );
};

const apiGetStudygroups = (page, query) => {
  const params =
    page === "all"
      ? {}
      : {
          page,
          per_page: 5,
          ...query,
        };

  return axios.get(API_STUDY_GROUPS, { params });
};

const apiGetStudygroupsAdmin = (page, query) => {
  const params =
    page === "all"
      ? {}
      : {
          page,
          per_page: 5,
          ...query,
        };

  return axios.get(API_ADMIN_STUDY_GROUPS, { params });
};

const apiUpdateStudygroup = (id, studygroup) =>
  axios.put(`${API_ADMIN_STUDY_GROUPS}/${id}`, studygroup);

const apiUpdateStudygroupSuperhost = (id, studygroup) =>
  axios.put(`${API_STUDY_GROUPS}/${id}`, studygroup);

const apiDeleteStudygroups = (studygroup_ids) => {
  return axios.post(`${API_ADMIN_STUDY_GROUPS}/destroy`, {
    group_ids: studygroup_ids,
  });
};

const apiGetStudygroupMembers = ({ id, params }) => {
  return axios.get(`${API_STUDY_GROUP_MEMBERS}${id}`, { params });
};

const apiGetAdminStudygroupMembers = (groupId, params) => {
  return axios.get(`${API_ADMIN_STUDY_GROUP_MEMBERS}${groupId}`, { params });
};

const apiSendInvites = (groupId, emails) => {
  return axios.post(`${API_ADMIN_STUDY_GROUP_INVITES}`, {
    group_invites: {
      group_id: groupId,
      emails,
    },
  });
};

const apiDeleteGroupMember = (id) => axios.delete(`${API_GROUP_INVITES}${id}`);

const apiGetGroupInvites = (groupId, params) => {
  return axios.get(
    `${API_ADMIN_STUDY_GROUP_INVITES}?group_id=${groupId}${params}`
  );
};

const apiDeleteInvite = (inviteId) => {
  return axios.delete(`${API_ADMIN_STUDY_GROUP_INVITES}/${inviteId}`);
};

const apiGetCourseMaterials = (groupId, params) => {
  return axios.get(`${API_ADMIN_GROUP_MATERIALS}?group_id=${groupId}${params}`);
};

const apiCreateCourseMaterial = (courseMaterial) => {
  return axios.post(API_ADMIN_GROUP_MATERIALS, courseMaterial);
};

const apiUpdateCourseMaterial = (id, courseMaterial) => {
  return axios.put(`${API_ADMIN_GROUP_MATERIALS}/${id}`, courseMaterial);
};

const apiDeleteCourseMaterials = (materialsIds) => {
  return axios.post(`${API_ADMIN_GROUP_MATERIALS}/destroy`, {
    group_material_ids: materialsIds,
  });
};

const apiDeleteInvitation = (id) => axios.delete(`${API_GROUP_INVITES}${id}`);

function* createStudygroup({ studygroup }) {
  try {
    if (
      yield select((store) => store.requests.indexOf("studygroup_create") != -1)
    )
      return;

    yield put(startRequest("studygroup_create"));

    const errors = validator.validate(studygroup, "studygroup");
    if (errors) {
      yield put(addValidateError(CREATE_STUDYGROUP, errors));
      yield put(endRequest("studygroup_create"));
      return;
    } else {
      yield put(cleanErrors(CREATE_STUDYGROUP));
    }

    const formData = getStudyGroupFormData(studygroup);

    yield call(apiCreateStudygroup, formData);

    toast.success("Studygroup successfully created!");

    yield put(push("/groups/"));
    yield put(endRequest("studygroup_create"));
  } catch (e) {
    yield put(endRequest("studygroup_create"));
    yield put(addErrorsSaga(CREATE_STUDYGROUP, e.response));
  }
}

function* getStudygroup({ id }) {
  try {
    const response = yield call(apiGetStudygroup, id);
    const studygroup = response.data.group;

    yield put(setStudygroup(studygroup));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getAdminStudygroup({ id }) {
  try {
    const response = yield call(apiAdminGetStudygroup, id);
    const studygroup = response.data.group;

    yield put(setStudygroup(studygroup));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getStudygroups({ page = 1, query = {} }) {
  try {
    const response = yield call(apiGetStudygroups, page, query);
    const studygroups = response.data.groups;
    const count = response.data.count;
    const total_pages = response.data.total_pages;
    yield put(
      saveStudygroups({ studygroups, count, page, total_pages, query })
    );
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getStudygroupsInfinite({ page = 1, query = {} }) {
  try {
    const response = yield call(apiGetStudygroups, page, query);
    const studygroups = response.data.groups;
    const count = response.data.count;
    const total_pages = response.data.total_pages;
    yield put(
      saveStudygroupsInfinite({ studygroups, count, page, total_pages, query })
    );
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getStudygroupsAdmin({ page = 1, query = {} }) {
  try {
    const response = yield call(apiGetStudygroupsAdmin, page, query);
    const studygroups = response.data.groups;
    const count = response.data.count;
    const total_pages = response.data.total_pages;
    yield put(
      saveStudygroups({ studygroups, count, page, total_pages, query })
    );
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* updateStudygroup({ studygroup }) {
  try {
    const errors = validator.validate(studygroup, "studygroup");
    if (errors) {
      yield put(addValidateError(UPDATE_STUDYGROUP, errors));
      return;
    } else {
      yield put(cleanErrors(UPDATE_STUDYGROUP));
    }

    const formData = getStudyGroupFormData(studygroup);

    yield call(apiUpdateStudygroup, studygroup.id, formData);

    toast.success("Studygroup successfully updated!");

    yield put(push("/groups/"));
  } catch (e) {
    yield put(addErrorsSaga(UPDATE_STUDYGROUP, e.response));
  }
}

function* updateStudygroupSuperhost({ studygroup }) {
  try {
    const errors = validator.validate(studygroup, "studygroup");
    if (errors) {
      yield put(addValidateError(UPDATE_STUDYGROUP, errors));
      return;
    } else {
      yield put(cleanErrors(UPDATE_STUDYGROUP));
    }

    const formData = getStudyGroupFormData(studygroup);

    yield call(apiUpdateStudygroupSuperhost, studygroup.id, formData);

    toast.success("Studygroup successfully updated!");

    yield put(push("/groups/my"));
  } catch (e) {
    yield put(addErrorsSaga(UPDATE_STUDYGROUP, e.response));
  }
}

function* deleteStudygroups({ studygroup_ids }) {
  if (!studygroup_ids.length) return;
  const studygroupIds = studygroup_ids;
  try {
    yield call(apiDeleteStudygroups, studygroupIds);

    toast.success("Studygroups successfully deleted!");

    const query = yield select(
      (state) => state.studygroups.studygroups.query
    ) || {};
    const page = yield select((state) => state.studygroups.studygroups.page) ||
      1;

    const user = yield select((state) => state.users.user);

    if (user.role === "admin") {
      yield call(getStudygroupsAdmin, { page, query });
    } else {
      yield call(getStudygroups, { page, query });
    }
  } catch (e) {
    yield put(addErrorsSaga(DELETE_STUDYGROUPS, e.response));
  }
}

function* getStudygroupMembers({ payload }) {
  try {
    const { data } = yield call(apiGetStudygroupMembers, payload);
    // const query = yield select(state => state.studygroups.studygroups.query) || {}
    // const page = yield select(state => state.studygroups.studygroups.page) || 1
    // yield call(getStudygroups, { page, query })
    yield put(setGroupMembers(data));
  } catch (e) {
    yield put(addErrorsSaga(DELETE_STUDYGROUPS, e.response));
  }
}

function* deleteStudyGroupMaterial({ payload }) {
  try {
    const response = yield call(apiDeleteStudyGroupsMaterials, payload);
    toast.success(response.data.message);
    yield put(updateGroupMaterialsAfterDelete(payload));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getStudyGroupMaterialsInfinity({ payload }) {
  try {
    const response = yield call(apiLoadStudyGroupsMaterial, payload);
    yield put(setGroupMaterialInfinity(response.data));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getStudyGroupMaterials({ payload }) {
  try {
    const response = yield call(apiLoadStudyGroupsMaterial, payload);
    yield put(setGroupMaterials(response.data));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* createStudyGroupMaterial({ payload }) {
  try {
    const response = yield call(apiCreateStudyGroupMaterial, payload);
    yield put(
      push(`/groups/${response.data.group_material.group_id}/resources`)
    );
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getStudyGroupIvites({ payload }) {
  try {
    const response = yield call(apiGetStudyGroupInvites, payload);
    yield put(setStudyGroupInvites(response.data));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getStudyGroupStatistic({ payload }) {
  try {
    const response = yield call(apiGetStudyGroupStatistic, payload);
    yield put(setStudyGroupStatistic(response.data));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getStudyGroupWebinars({ payload }) {
  try {
    const response = yield call(apiGetStudyGroupWebinars, payload);
    yield put(setStudyGroupWebinars(response.data));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* deleteStudyGroupWebinars({ ids, query }) {
  try {
    yield call(apiDeleteWebinars, ids);
    toast.success("Webinars have been deleted");
    const response = yield call(apiGetStudyGroupWebinars, query);
    yield put(setStudyGroupWebinars(response.data));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getAdminStudygroupMembers({ groupId, params = {} }) {
  try {
    // const {page, per_page, query} = params || {};
    // const urlParams = getUrl({
    //   ...query,
    //   page,
    //   per_page,
    // });
    const { data } = yield call(apiGetAdminStudygroupMembers, groupId, params);
    const members = data.members;
    const count = data.count;
    const total_pages = data.total_pages;

    yield put(
      setAdminGroupMembers({
        members,
        count,
        total_pages,
        page: params.page || 1,
        per_page: params.per_page,
        query: params.query,
      })
    );
  } catch (e) {
    console.log(e);
    yield put(addErrorsSaga(GET_ADMIN_GROUP_MEMBERS, e.response));
  }
}

function* getGroupInvites({ groupId, params }) {
  try {
    const { page, per_page, query } = params || {};
    const urlParams = getUrl({
      ...query,
      page,
      per_page,
    });
    const { data } = yield call(apiGetGroupInvites, groupId, urlParams);
    const invites = data.invites;
    const count = data.count;
    const total_pages = data.total_pages;

    yield put(
      setGroupInvites({
        invites,
        count,
        total_pages,
        page: page || 1,
        per_page,
        query,
      })
    );
  } catch (e) {
    console.log(e);
    yield put(addErrorsSaga(GET_GROUP_INVITES, e.response));
  }
}

function* sendGroupInvites({ groupId, emails }) {
  try {
    const { data } = yield call(apiSendInvites, groupId, emails);

    if (data.message === "Invites was sended") {
      yield put(goBack());
      toast.success("Invites successfully sent!");
    } else {
      yield put(addErrorsSaga(SEND_GROUP_INVITES, e.response));
      toast.success("Something went wrong!");
    }
  } catch (e) {
    yield put(addErrorsSaga(SEND_GROUP_INVITES, e.response));
    toast.success("Something went wrong!");
  }
}

function* deleteInvite({ inviteId }) {
  try {
    const { data } = yield call(apiDeleteInvite, inviteId);

    if (data.message === "Invitation canceled") {
      yield put(deleteInviteSuccess(inviteId));
      toast.success("Invite successfully canceled!");
    } else {
      yield put(addErrorsSaga(DELETE_INVITE, e.response));
      toast.success("Something went wrong!");
    }
  } catch (e) {
    yield put(addErrorsSaga(DELETE_INVITE, e.response));
    toast.success("Something went wrong!");
  }
}

function* handleGetCourseMaterials({ groupId, params }) {
  try {
    const { page, per_page, query } = params || {};
    const urlParams = getUrl({
      ...query,
      page,
      per_page,
    });

    const { data } = yield call(apiGetCourseMaterials, groupId, urlParams);
    const courseMaterials = data.group_materials;
    const count = data.count;
    const total_pages = data.total_pages;

    yield put(
      setCourseMaterials({
        courseMaterials,
        count,
        total_pages,
        page: page || 1,
        per_page,
        query,
      })
    );
  } catch (e) {
    console.log(e);
    yield put(addErrorsSaga(GET_GROUP_MEMBERS, e.response));
  }
}

function* createCourseMaterial({ courseMaterial }) {
  try {
    yield put(startRequest("course_material_create"));

    const errors = validator.validate(courseMaterial, "courseMaterial");
    if (errors) {
      yield put(addValidateError(CREATE_COURSE_MATERIAL, errors));
      yield put(endRequest("course_material_create"));
      return;
    } else {
      yield put(cleanErrors(CREATE_COURSE_MATERIAL));
    }

    const query = yield select(
      (state) => state.studygroups.courseMaterials.query
    ) || {};
    const page = yield select(
      (state) => state.studygroups.courseMaterials.page
    ) || 1;
    const per_page = yield select(
      (state) => state.studygroups.courseMaterials.per_page
    ) || 5;
    const studygroup = yield select((state) => state.studygroups.studygroup) ||
      {};
    const requestData = yield call(
      formatRequestData,
      courseMaterial,
      "group_material"
    );

    yield call(apiCreateCourseMaterial, requestData);
    yield put(getCourseMaterials(studygroup.id, { page, per_page, query }));
    yield put(hideModal());

    toast.success("Course material successfully created!");

    // yield put(push('/groups/'))
    yield put(endRequest("course_material_create"));
  } catch (e) {
    yield put(endRequest("course_material_create"));
    yield put(addErrorsSaga(CREATE_COURSE_MATERIAL, e.response));
  }
}

function* updateCourseMaterial({ courseMaterial }) {
  try {
    yield put(startRequest("course_material_update"));

    const errors = validator.validate(courseMaterial, "courseMaterial");
    if (errors) {
      yield put(addValidateError(UPDATE_COURSE_MATERIAL, errors));
      yield put(endRequest("course_material_update"));
      return;
    } else {
      yield put(cleanErrors(UPDATE_COURSE_MATERIAL));
    }
    const query = yield select(
      (state) => state.studygroups.courseMaterials.query
    ) || {};
    const page = yield select(
      (state) => state.studygroups.courseMaterials.page
    ) || 1;
    const per_page = yield select(
      (state) => state.studygroups.courseMaterials.per_page
    ) || 5;
    const studygroup = yield select((state) => state.studygroups.studygroup) ||
      {};
    const requestData = yield call(
      formatRequestData,
      courseMaterial,
      "group_material"
    );

    yield call(apiUpdateCourseMaterial, courseMaterial.id, requestData);
    yield put(getCourseMaterials(studygroup.id, { page, per_page, query }));
    yield put(hideModal());

    toast.success("Course material successfully updated!");

    // yield put(push('/groups/'))
    yield put(endRequest("course_material_update"));
  } catch (e) {
    yield put(endRequest("course_material_update"));
    yield put(addErrorsSaga(UPDATE_COURSE_MATERIAL, e.response));
  }
}

function* deleteCourseMaterials({ materialsIds }) {
  if (!materialsIds.length) return;

  try {
    yield call(apiDeleteCourseMaterials, materialsIds);

    toast.success("Course materials successfully deleted!");

    const query = yield select(
      (state) => state.studygroups.courseMaterials.query
    ) || {};
    const page = yield select(
      (state) => state.studygroups.courseMaterials.page
    ) || 1;
    const per_page = yield select(
      (state) => state.studygroups.courseMaterials.per_page
    ) || 5;
    const studygroup = yield select((state) => state.studygroups.studygroup) ||
      {};

    yield put(getCourseMaterials(studygroup.id, { page, per_page, query }));
  } catch (e) {
    yield put(addErrorsSaga(DELETE_COURSE_MATERIALS, e.response));
  }
}

function* joinGroupFromLink({ payload }) {
  try {
    const isLogged = yield select((store) => !_.isEmpty(store.users.user));
    if (isLogged) {
      const res = yield call(apiJoinStudyGroup, payload);
      storage.removeJoinGroupId();
      toast.success("You successfully join to the MedMeeting!");
      yield put(
        push(`/groups/${res.data.group_invite.group_id}/course-materials`)
      );
    } else {
      storage.setJoinGroupId(payload);
      toast.warn("To proceed, you must be logged in");
      yield put(push("/users/sign_in"));
    }
  } catch (e) {
    console.log(e);
  }
}

function* deleteGroupMember({ payload }) {
  try {
    const { id, groupId, params } = payload;
    yield call(apiDeleteGroupMember, id);
    yield call(getStudygroupMembers, { payload: { id: groupId, params } });
  } catch (e) {
    console.log(e);
  }
}

function* deleteGroupInvite({ payload }) {
  try {
    const { id, params } = payload;
    yield call(apiDeleteInvitation, id);
    const { id: groupId, ...restParams } = params;
    yield call(getStudygroupMembers, {
      payload: { id: groupId, params: restParams },
    });
  } catch (e) {
    console.log(e);
  }
}

function* userLeaveGroup() {
  try {
    const { id, title } = yield select((state) => state.studygroups.studygroup);
    yield call(apiLeaveGroupUser, id);
    toast.success(`You have successfully left ${title}`);
    yield put(push("/groups/my"));
  } catch (e) {
    console.log(e);
  }
}

function* userRequestToJoinGroup({ groupId }) {
  try {
    const { data } = yield call(apiUserRequestToJoinGroup, groupId);
    yield put(
      updateStudyGroupRequest({
        groupId,
        data: { is_request_sended: true, invite: [data.invite] },
      })
    );
    toast.success(
      "Thank you for requesting to join. Please allow up to 72 hours for your request to be reviewed"
    );
  } catch (e) {
    yield put(addErrorsSaga(USER_REQUEST_TO_JOIN_GROUP, e.response));
  }
}

function* userCancelRequest({ payload }) {
  try {
    console.log("Cancel request", payload);
    yield call(apiDeleteGroupMember, payload.inviteId);
    yield put(
      updateStudyGroupRequest({
        groupId: payload.groupId,
        data: { is_request_sended: false, invite: [] },
      })
    );
    toast.success("Your request to join has been cancelled.");
  } catch (e) {
    yield put(addErrorsSaga(USER_CANCEL_REQUEST_STUDY_GROUP, e.response));
  }
}

function* __createStudygroup() {
  yield takeEvery(CREATE_STUDYGROUP, createStudygroup);
}

function* __getStudygroup() {
  yield takeEvery(GET_STUDYGROUP, getStudygroup);
}

function* __getAdminStudygroup() {
  yield takeEvery(GET_ADMIN_STUDYGROUP, getAdminStudygroup);
}

function* __getStudygroups() {
  yield takeEvery(GET_STUDYGROUPS, getStudygroups);
}

function* __getStudygroupsInfinite() {
  yield takeEvery(GET_STUDYGROUPS_INFINITE, getStudygroupsInfinite);
}

function* __getStudygroupsAdmin() {
  yield takeEvery(GET_STUDYGROUPS_ADMIN, getStudygroupsAdmin);
}

function* __updateStudygroup() {
  yield takeEvery(UPDATE_STUDYGROUP, updateStudygroup);
}

function* __updateStudygroupSuperhost() {
  yield takeEvery(UPDATE_STUDYGROUP_SUPERHOST, updateStudygroupSuperhost);
}

function* __deleteStudygroups() {
  yield takeEvery(DELETE_STUDYGROUPS, deleteStudygroups);
}

function* __getStudygroupMembers() {
  yield takeEvery(GET_GROUP_MEMBERS, getStudygroupMembers);
}

function* __getStudygroupMaterialsInfinity() {
  yield takeEvery(LOAD_GROUP_MATERIAL, getStudyGroupMaterialsInfinity);
}

function* __getStudygroupMaterials() {
  yield takeEvery(GET_GROUP_MATERIAL, getStudyGroupMaterials);
}

function* __createStudyGroupMaterial() {
  yield takeEvery(CREATE_STUDY_GROUP_MATERIALS, createStudyGroupMaterial);
}

function* __deleteStudyGroupMaterial() {
  yield takeEvery(DELETE_STUDY_GROUP_MATERIAL, deleteStudyGroupMaterial);
}

function* __getStudyGroupInvites() {
  yield takeEvery(GET_STUDY_GROUP_INVITES, getStudyGroupIvites);
}

function* __getStudyGroupStatistic() {
  yield takeEvery(GET_STUDY_GROUP_STATISTIC, getStudyGroupStatistic);
}

function* __getStudyGroupWebinars() {
  yield takeEvery(GET_STUDY_GROUP_WEBINARS, getStudyGroupWebinars);
}

function* __deleteStudyGroupWebinars() {
  yield takeEvery(DELETE_STUDY_GROUP_WEBINARS, deleteStudyGroupWebinars);
}

function* __getAdminStudygroupMembers() {
  yield takeEvery(GET_ADMIN_GROUP_MEMBERS, getAdminStudygroupMembers);
}

function* __sendGroupInvites() {
  yield takeEvery(SEND_GROUP_INVITES, sendGroupInvites);
}

function* __getGroupInvites() {
  yield takeEvery(GET_GROUP_INVITES, getGroupInvites);
}

function* __deleteInvite() {
  yield takeEvery(DELETE_INVITE, deleteInvite);
}

function* __getCourseMaterials() {
  yield takeEvery(GET_COURSE_MATERIALS, handleGetCourseMaterials);
}

function* __createCourseMaterial() {
  yield takeEvery(CREATE_COURSE_MATERIAL, createCourseMaterial);
}

function* __updateCourseMaterial() {
  yield takeEvery(UPDATE_COURSE_MATERIAL, updateCourseMaterial);
}

function* __deleteCourseMaterials() {
  yield takeEvery(DELETE_COURSE_MATERIALS, deleteCourseMaterials);
}

function* __joinGroupFromLink() {
  yield takeEvery(JOIN_GROUP_FROM_LINK, joinGroupFromLink);
}

function* __deleteGroupMember() {
  yield takeEvery(DELETE_GROUP_MEMBER, deleteGroupMember);
}

function* __deleteGroupMemberInvite() {
  yield takeEvery(DELETE_GROUP_INVITE, deleteGroupInvite);
}

function* __userLeaveGroup() {
  yield takeEvery(USER_LEAVE_GROUP, userLeaveGroup);
}

function* __userRequestToJoinGroup() {
  yield takeEvery(USER_REQUEST_TO_JOIN_GROUP, userRequestToJoinGroup);
}

function* __userCancelRequest() {
  yield takeEvery(USER_CANCEL_REQUEST_STUDY_GROUP, userCancelRequest);
}

export {
  __createStudyGroupMaterial,
  __createStudygroup,
  __deleteStudyGroupMaterial,
  __deleteStudyGroupWebinars,
  __deleteStudygroups,
  __getStudyGroupInvites,
  __getStudyGroupStatistic,
  __getStudyGroupWebinars,
  __getStudygroup,
  __getStudygroupMaterials,
  __getStudygroupMaterialsInfinity,
  __getStudygroupMembers,
  __getAdminStudygroupMembers,
  __getStudygroups,
  __getStudygroupsAdmin,
  __updateStudygroup,
  __sendGroupInvites,
  __getGroupInvites,
  __deleteInvite,
  __getCourseMaterials,
  __createCourseMaterial,
  __updateCourseMaterial,
  __deleteCourseMaterials,
  __deleteGroupMember,
  __deleteGroupMemberInvite,
  __getStudygroupsInfinite,
  __joinGroupFromLink,
  __userLeaveGroup,
  __getAdminStudygroup,
  __updateStudygroupSuperhost,
  __userRequestToJoinGroup,
  __userCancelRequest,
};



// WEBPACK FOOTER //
// ./src/sagas/admin/studygroups.js